import React, { useState } from 'react';
import { useController } from 'react-hook-form';
import { MessageDescriptor } from '@lingui/core';
import { t } from '@lingui/macro';

import { States as states } from '@rover/constants/src/geography/unitedStates.constants';
import { Close, Plus } from '@rover/icons';
import { Box, Flex, Link } from '@rover/kibble/core';
import { ConfigureFieldInterface, ZodSchemaOrEffects } from '@rover/kibble/forms';
import translateFieldError from '@rover/kibble/forms/core/translateFieldError';
import SelectField from '@rover/kibble/forms/fields/SelectField';
import TextField from '@rover/kibble/forms/fields/TextField';

import CountrySection from './CountrySection';
import PlatformLocationInput from './PlatformLocationInput';

export interface KibbleAddressFieldsProps<Z extends ZodSchemaOrEffects> {
  addressField: ConfigureFieldInterface<Z>;
  cityField: ConfigureFieldInterface<Z>;
  stateField: ConfigureFieldInterface<Z>;
  zipField: ConfigureFieldInterface<Z>;
  countryField: ConfigureFieldInterface<Z>;
  addressLine2Field?: ConfigureFieldInterface<Z>;
  countryCheckboxesField: ConfigureFieldInterface<Z>;
  showStateField?: boolean;
  locale?: string;
  setValue: (name: string, value: string, options?: { shouldDirty?: boolean }) => void;
  clearErrors: (name?: string) => void;
  getValues?: (name: string) => string;
  disableCountry?: boolean;
}

const KibbleAddressFields = <Z extends ZodSchemaOrEffects>({
  addressField,
  cityField,
  stateField,
  zipField,
  countryField,
  addressLine2Field,
  showStateField = true,
  countryCheckboxesField,
  locale = 'en-us',
  setValue,
  clearErrors,
  disableCountry = false,
  getValues,
}: KibbleAddressFieldsProps<Z>): JSX.Element => {
  const hasAddressLine2Value = getValues && getValues('addressLine2');
  const [isAddressLine2Expanded, setIsAddressLine2Expanded] = useState<boolean>(
    !!hasAddressLine2Value
  );

  const { field: addressFieldValue } = useController({
    control: addressField.control,
    name: addressField.name,
  });

  const handleInputChange = (data: {
    value?: string | MessageDescriptor;
    postalCode?: string;
    countryCode?: string;
    city?: string;
  }): void => {
    const finalValue = String(data.value || '');
    addressFieldValue.onChange(finalValue);

    if (setValue) {
      // address format format = "123 Main St, City, State, Country"
      const parts = finalValue.split(',').map((part) => part.trim());
      const [addressLine = '', city = '', state = ''] = parts;
      setValue('zip', data.postalCode || '');
      !disableCountry && setValue('countryCode', data.countryCode || '', { shouldDirty: true });
      addressLine && setValue('addressLine1', addressLine);
      setValue('city', data.city || city);
      state && setValue('state', state);
      clearErrors('addressLine1');
    }
  };

  const handleRemoveAddressLine2 = (): void => {
    setIsAddressLine2Expanded(false);
    setValue && setValue('addressLine2', '');
  };

  return (
    <Box>
      <Flex flexDirection="column" gap="2x">
        <Box>
          <PlatformLocationInput
            inputValue={addressFieldValue.value || ''}
            onChange={handleInputChange}
            errorMessage={
              addressField.fieldError ? translateFieldError(addressField.fieldError) : undefined
            }
            setValue={setValue}
          />
        </Box>

        {addressLine2Field && (
          <Box>
            {!isAddressLine2Expanded ? (
              <Link
                onClick={() => setIsAddressLine2Expanded(true)}
                href=""
                fontWeight="semibold"
                icon={Plus}
              >
                {t`Add unit, suite, or other`}
              </Link>
            ) : (
              <Box my="2x">
                <TextField
                  {...addressLine2Field}
                  primaryLabel={t`Apartment, Unit, Suite`}
                  marginBottom="2x"
                  sensitive
                />
                <Link onClick={handleRemoveAddressLine2} href="" fontWeight="semibold" icon={Close}>
                  {t`Remove`}
                </Link>
              </Box>
            )}
          </Box>
        )}

        <Flex flexDirection={['column', 'row']} alignItems="flex-start">
          <Box mr={['0x', '4x']} width="100%">
            <TextField {...cityField} primaryLabel={t`City`} placeholder={t`Enter city`} />
          </Box>

          <Flex flexDirection="row" gap="4x" width="100%">
            {showStateField && (
              <Box flex="1">
                <SelectField
                  {...stateField}
                  primaryLabel={t`State`}
                  options={states.map((s) => ({ value: s.code, label: s.name }))}
                  placeholder={t`Select state`}
                  required
                />
              </Box>
            )}
            <Box flex="1">
              <TextField {...zipField} primaryLabel={t`ZIP Code`} placeholder="12345" sensitive />
            </Box>
          </Flex>
        </Flex>
        <CountrySection
          countryField={countryField}
          countryCheckboxesField={countryCheckboxesField}
          locale={locale}
          disableCountry={disableCountry}
          getValues={getValues}
        />
      </Flex>
    </Box>
  );
};

export default KibbleAddressFields;
